import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { TaskCardType } from '@econtractor/types';
import * as apiTask from 'api/apiTask';
import { useToast } from 'context/ToastContext';
import spinnerTitle from 'components/others/spinnerTitle';
import TaskDueDateFields from './TaskDueDateFields';
import {
    applyEndDateChange,
    applyEndTimeChange,
    applyMultiDayChange,
    applyStartDateChange,
    applyWithTimeChange,
    buildTermHistoryComment,
    dueDatesFromTask,
    normalizeDueDateValues,
    type TaskDueDateValues,
} from './helpers/taskDueDateHelpers';

interface TaskTermEditFormProps {
    task: TaskCardType;
    onCancel: () => void;
    onSaved: (task: TaskCardType) => void;
}

const TaskTermEditForm = ({ task, onCancel, onSaved }: TaskTermEditFormProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { showToast } = useToast();
    const [values, setValues] = useState<TaskDueDateValues>(() => dueDatesFromTask(task));
    const [saving, setSaving] = useState(false);

    const patch = (partial: Partial<TaskDueDateValues>) => {
        setValues((prev) => ({ ...prev, ...partial }));
    };

    const handleSave = async () => {
        setSaving(true);
        const normalized = normalizeDueDateValues(values);
        const historyComment = buildTermHistoryComment(normalized);
        const result = await apiTask.updateTaskDates(task.ID, {
            ...normalized,
            historyComment,
        });
        setSaving(false);

        if (!result?.stat || !result.task) {
            showToast(t('common:messError'), 'danger');
            return;
        }

        showToast(t('messTermUpdated'), 'success');
        onSaved(result.task);
    };

    return (
        <div className="mt-2">
            <TaskDueDateFields
                idPrefix={`task-${task.ID}-term`}
                hideLabel
                multiDay={values.multiDay}
                withTime={values.withTime}
                dateStart={values.dateStart}
                dateEnd={values.dateEnd}
                endTime={values.endTime}
                onMultiDayChange={(checked) => patch(applyMultiDayChange(values, checked))}
                onWithTimeChange={(checked) => patch(applyWithTimeChange(values, checked))}
                onStartDateChange={(iso) => patch(applyStartDateChange(values, iso))}
                onEndDateChange={(iso) => patch(applyEndDateChange(values, iso))}
                onEndTimeChange={(time) => patch(applyEndTimeChange(values, time))}
            />

            <div className="text-end">
                <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="mb-1 min70px"
                    style={{ height: 24 }}
                    disabled={saving}
                    onClick={onCancel}
                >
                    {t('common:buttons.cancel')}
                </Button>
                <Button
                    type="button"
                    variant="success"
                    size="sm"
                    className="mb-1 ms-2 min70px"
                    style={{ height: 24 }}
                    disabled={saving}
                    onClick={() => void handleSave()}
                >
                    {spinnerTitle(t('common:buttons.change'), saving, t('common:buttons.saving'))}
                </Button>
            </div>
        </div>
    );
};

export default TaskTermEditForm;
